// src/workspaces/verificationApi.ts
import { apiClient } from "@maat/api-base";
var verificationApi = {
  // Identity — per-user, and the caller's own record.
  getMyIdentityVerification: async () => {
    return await apiClient.get("/main/api/verification/identity");
  },
  submitIdentityVerification: async (data) => {
    return await apiClient.post("/main/api/verification/identity", data);
  },
  // Automated verification (Didit). NO ARGUMENTS, and that is the contract, not an
  // omission: the subject, the callback URL and the workflow are all derived
  // server-side, and the endpoint's Zod schema STRIPS anything a caller sends.
  startIdentityVerificationSession: async () => {
    return await apiClient.post("/main/api/verification/identity/session");
  },
  // Each file is uploaded under a field name equal to its document_type, which
  // is how the backend maps the upload to a VerificationDocumentType.
  uploadIdentityDocument: async ({
    documentType,
    file
  }) => {
    const formData = new FormData();
    formData.append(documentType, file);
    return await apiClient.post("/main/api/verification/identity/documents", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteIdentityDocument: async (documentId) => {
    await apiClient.delete(`/main/api/verification/identity/documents/${documentId}`);
  },
  // Verified records: removal goes through an admin, so the user requests it.
  requestIdentityDocumentRemoval: async (documentId) => {
    await apiClient.post(
      `/main/api/verification/identity/documents/${documentId}/request-removal`
    );
  },
  cancelIdentityDocumentRemoval: async (documentId) => {
    await apiClient.delete(
      `/main/api/verification/identity/documents/${documentId}/request-removal`
    );
  },
  /**
   * ANOTHER person's identity badge — the only identity read here whose subject is
   * not the caller. Four allow-listed fields (verified · country · document kind ·
   * month) and nothing that could identify the document: it answers "what may a host
   * be told about their guest?", not "what do I know about MY verification?".
   */
  getIdentityBadge: async (userId) => {
    return await apiClient.get(
      `/main/api/verification/identity/${userId}/badge`
    );
  },
  // Business — the active workspace, LEGAL_ENTITY only.
  getBusinessVerification: async () => {
    return await apiClient.get("/main/api/verification/business");
  },
  submitBusinessVerification: async (data) => {
    return await apiClient.post("/main/api/verification/business", data);
  },
  // Each file is uploaded under a field name equal to its document_type.
  uploadBusinessDocument: async ({
    documentType,
    file
  }) => {
    const formData = new FormData();
    formData.append(documentType, file);
    return await apiClient.post("/main/api/verification/business/documents", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteBusinessDocument: async (documentId) => {
    await apiClient.delete(`/main/api/verification/business/documents/${documentId}`);
  },
  requestBusinessDocumentRemoval: async (documentId) => {
    await apiClient.post(
      `/main/api/verification/business/documents/${documentId}/request-removal`
    );
  },
  cancelBusinessDocumentRemoval: async (documentId) => {
    await apiClient.delete(
      `/main/api/verification/business/documents/${documentId}/request-removal`
    );
  },
  // Admin console — the unified review queue, admin_token session.
  adminListVerifications: async (params = {}) => {
    const { removal, ...rest } = params;
    return await apiClient.get("/main/api/verification/admin/list", {
      params: { ...rest, ...removal ? { removal: "requested" } : {} }
    });
  },
  adminVerificationStats: async () => {
    return await apiClient.get("/main/api/verification/admin/stats");
  },
  adminGetVerification: async (kind, subjectId) => {
    return await apiClient.get(`/main/api/verification/admin/${kind}/${subjectId}`);
  },
  reviewIdentityVerification: async (userId, data) => {
    return await apiClient.post(`/main/api/verification/identity/${userId}/review`, data);
  },
  reviewBusinessVerification: async (workspaceId, data) => {
    return await apiClient.post(`/main/api/verification/business/${workspaceId}/review`, data);
  },
  // Admin: approve/reject a single document independently (identity + business).
  adminReviewDocument: async (documentId, data) => {
    return await apiClient.post(`/main/api/verification/documents/${documentId}/review`, data);
  },
  // Admin: resolve a pending document-removal request (confirm = delete, reject = keep).
  adminResolveDocumentRemoval: async (documentId, data) => {
    return await apiClient.post(
      `/main/api/verification/documents/${documentId}/resolve-removal`,
      data
    );
  },
  /**
   * Admin: pull the current decision from Didit for one record and apply it. The
   * session is read off the record `userId` resolves to, so a caller cannot aim a
   * resync at an arbitrary vendor session — the Zod body allow-list holds `override`
   * and nothing else. `override` is NOT a convenience flag: without it the server
   * 409s whenever a platform admin reviewed the record more recently than the
   * provider decided, because a vendor snapshot must never quietly reverse a human
   * fraud call. Passing it is a deliberate second action with its own audit row.
   */
  adminResyncIdentityVerification: async (userId, override) => {
    return await apiClient.post(`/main/api/verification/admin/identity/${userId}/resync`, {
      override
    });
  },
  // Didit webhook inbox (admin_token session). ENVELOPE ONLY: there is no
  // `getPayload` here and no endpoint behind one — a quarantined row holds the
  // verbatim signed body, carrying the document fields the identity row is
  // encrypted to protect.
  adminListDiditInbox: async (params = {}) => {
    return await apiClient.get("/main/api/verification/admin/webhook-inbox", { params });
  },
  adminDiditInboxStats: async () => {
    return await apiClient.get("/main/api/verification/admin/webhook-inbox/stats");
  }
};

export {
  verificationApi
};
